import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SebasLogo } from "@/components/brand/SebasLogo";
import { Button } from "@/components/ui/Button";
import { api, ApiError } from "@/lib/api";

type Preview = {
  invite: { team: string; role: string; expiresAt: string };
  expired: boolean;
  used: boolean;
};

/** Landing page for an invite link. Auth is already enforced by RequireAuth. */
export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Preview>(`/v1/invites/${token}`)
      .then(setPreview)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [token]);

  const accept = () => {
    setBusy(true);
    setError(null);
    api<{ teamId: string }>(`/v1/invites/${token}/accept`, { method: "POST" })
      .then(() => navigate("/teams"))
      .catch((e) => {
        setError(e instanceof ApiError ? e.message : String(e));
        setBusy(false);
      });
  };

  const blocked = preview?.expired || preview?.used;

  return (
    <div className="grid min-h-dvh place-items-center bg-surface px-5">
      <div className="flex w-full max-w-sm flex-col items-start gap-5">
        <SebasLogo />
        {error ? (
          <>
            <h1 className="font-display text-4xl font-semibold">Invite problem</h1>
            <p className="text-sm text-ink-muted">{error}</p>
            <Button variant="secondary" onClick={() => navigate("/notes")}>Go to my notes</Button>
          </>
        ) : !preview ? (
          <p className="text-sm text-ink-faint">Checking the invite…</p>
        ) : (
          <>
            <h1 className="font-display text-4xl font-semibold">
              Join
              <br />
              {preview.invite.team}
            </h1>
            <p className="text-sm text-ink-muted">
              {blocked
                ? preview.used
                  ? "This invite has already been used."
                  : "This invite has expired. Ask for a new one."
                : `You have been invited as ${preview.invite.role}.`}
            </p>
            {!blocked ? (
              <Button size="lg" onClick={accept} disabled={busy}>
                {busy ? "Joining…" : "Accept invite"}
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => navigate("/notes")}>Go to my notes</Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
